import { Slot } from "@radix-ui/react-slot";
import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from "react";
import { cn } from "../lib/cn";
import { focusRing, hoverTransition, minTarget } from "../lib/styles";
import { Spinner } from "./Spinner";

/** doc 40 §13. (H) One primary action per view. */
export type ButtonVariant = "primary" | "secondary" | "tertiary" | "ghost" | "destructive";
export type ButtonSize = "sm" | "md" | "lg";

/**
 * doc 40 §13, verbatim.
 *
 * Every variant carries `border` with a colour — a transparent one where the
 * spec says "none" — so a button never changes size when its variant changes.
 * That is why `primary` is `border-primary` rather than `border-transparent`:
 * the fill and the edge are the same colour, so the 1px reads as fill.
 */
const VARIANT: Record<ButtonVariant, string> = {
  /** `fuchsia-600` with white at 4.71:1. */
  primary:
    "bg-primary border-primary text-on-primary " +
    "hover:bg-primary-hover hover:border-primary hover:shadow-button-hover " +
    "active:bg-primary-active",
  secondary:
    "bg-default border-control text-default shadow-card " +
    "hover:bg-hover hover:border-strong active:bg-active",
  /**
   * doc 40 §13's "Tertiary / ghost" row conflates two jobs, and splitting
   * them matters: `tertiary` is a brand-coloured text action ("View all",
   * "Undo"), while `ghost` is neutral chrome.
   *
   * (H) doc 40 §1 habit 4: colour is an accent, not a surface. Six
   * brand-pink icons across the top nav is brand-as-decoration — the exact
   * failure doc 40 §16 records — so an icon that is chrome takes
   * `text-secondary` and earns `text-default` on hover.
   */
  tertiary:
    "bg-transparent border-transparent text-primary hover:bg-primary-weak active:bg-active",
  ghost:
    "bg-transparent border-transparent text-secondary hover:bg-hover hover:text-default active:bg-active",
  destructive:
    "bg-danger border-danger text-on-danger " +
    "hover:shadow-button-hover active:opacity-90",
};

/** doc 40 §13: `bg-subtle` fill, `text-disabled` label, `border-default` edge. */
const DISABLED = "bg-subtle border-default text-disabled shadow-none cursor-not-allowed";

/** Heights are 28 / 32 / 40px. `md` at 32px is the default control height. */
const SIZE: Record<ButtonSize, { base: string; text: string; icon: string }> = {
  sm: { base: "h-8 px-3 gap-1.5", text: "text-xs", icon: "h-8 w-8" },
  md: { base: "h-9 px-3.5 gap-2", text: "text-sm", icon: "h-9 w-9" },
  lg: { base: "h-10 px-4 gap-2", text: "text-sm", icon: "h-10 w-10" },
};

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "className"> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Renders a spinner and blocks interaction. Keeps the label so width is stable. */
  loading?: boolean;
  loadingLabel?: string;
  /** Renders as the child element (a router Link, say) instead of a `<button>`. */
  asChild?: boolean;
  /** (H) doc 40 §13: no icon-only button without an accessible label. */
  iconOnly?: boolean;
  className?: string;
  children?: ReactNode;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = "secondary",
    size = "md",
    loading = false,
    loadingLabel = "Working",
    asChild = false,
    iconOnly = false,
    className,
    children,
    disabled,
    type = "button",
    ...rest
  },
  ref,
) {
  const isDisabled = disabled === true || loading;
  const Comp = asChild ? Slot : "button";

  if (iconOnly && !rest["aria-label"]) {
    throw new Error(
      "Button: iconOnly requires an aria-label (doc 40 §13 — no icon-only button " +
        "without an accessible label).",
    );
  }

  return (
    <Comp
      ref={ref}
      type={asChild ? undefined : type}
      disabled={asChild ? undefined : isDisabled}
      aria-disabled={isDisabled || undefined}
      aria-busy={loading || undefined}
      className={cn(
        // doc 40 §7: 4px radius — buttons are the most-used case for it.
        "inline-flex select-none items-center justify-center whitespace-nowrap rounded-sm border",
        // (H) doc 41 §6: buttons are verbs naming the outcome, at weight 500.
        "font-semibold",
        // 16px icons, never clickable in their own right.
        "[&_svg]:pointer-events-none [&_svg]:shrink-0",
        minTarget,
        focusRing,
        hoverTransition,
        SIZE[size].text,
        iconOnly ? SIZE[size].icon : SIZE[size].base,
        isDisabled ? DISABLED : VARIANT[variant],
        className,
      )}
      {...rest}
    >
      {/* Radix's `Slot` requires EXACTLY ONE element child, so when `asChild`
          is set the children are passed straight through — a `{loading ? … :
          null}` sibling counts as a second child and throws at render time.
          A button rendering as a router `<Link>` cannot be in flight anyway;
          navigation is not an operation with a pending state here. */}
      {asChild ? (
        children
      ) : (
        <>
          {loading ? <Spinner label={loadingLabel} /> : null}
          {children}
        </>
      )}
    </Comp>
  );
});
